<template>
  <div class="p-4">
    <!-- Sin contratos -->
    <div v-if="contracts.length === 0" class="text-center py-5">
      <i class="ri-file-list-line text-muted" style="font-size: 3rem;"></i>
      <p class="text-muted mt-3 mb-4">Este colaborador no tiene contratos vigentes.</p>
      <wz-button
        variant="primary"
        @click="$router.push({ name: 'contracts-create', query: { employeeId: employeeId } })"
      >
        <i class="ri-add-line mr-1"></i>Crear contrato
      </wz-button>
    </div>

    <!-- Con contrato(s) -->
    <template v-else>
      <!-- Selector de contrato (solo si hay más de 1) -->
      <div v-if="contracts.length > 1" class="flex flex-wrap mb-4 contract-pills -mx-2">
        <wz-button
          v-for="(c, idx) in contracts"
          :key="c.id"
          size="sm"
          class="mr-2 mb-2"
          :variant="activeContractIndex === idx ? 'primary' : 'light'"
          @click="activeContractIndex = idx"
        >
          <i class="ri-building-line mr-1"></i>{{ companyOf(c) }}
          <span class="ml-1 text-muted text-sm" v-if="c.number">N° {{ c.number }}</span>
        </wz-button>
      </div>

      <div class="flex justify-between items-start mb-4">
        <div>
          <wz-badge :variant="contractStatusBadge.color">{{ contractStatusBadge.label }}</wz-badge>
          <wz-badge v-if="currentContract.hasRegisteredRenewal" variant="success" class="ml-1">RENOVADO</wz-badge>
          <span v-if="currentContract.number" class="ml-2 text-muted text-sm">
            N° {{ currentContract.number }}
          </span>
        </div>
        <div class="flex">
          <wz-button
            variant="light"
            size="sm"
            class="mr-2"
            @click="$router.push({ name: 'contracts-list', query: { employeeId: employeeId } })"
          >
            Ver todos los contratos
          </wz-button>
          <wz-button
            variant="light"
            size="sm"
            class="mr-2"
            @click="downloadDocument"
            :disabled="downloadingDoc"
          >
            <i class="ri-file-word-line mr-1"></i>
            {{ downloadingDoc ? 'Generando...' : 'Generar documento' }}
          </wz-button>
          <wz-button
            variant="primary"
            size="sm"
            @click="$router.push({ name: 'contracts-edit', params: { id: currentContract.id } })"
          >
            <i class="ri-edit-line mr-1"></i>Editar contrato
          </wz-button>
        </div>
      </div>

      <!-- Empresa / Sede / Área -->
      <div class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Empresa</small>
          <strong>{{ companyName }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Sede</small>
          <strong>{{ workSiteName }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Área</small>
          <strong>{{ areaName }}</strong>
        </div>
      </div>

      <!-- Cargo / Jefe / Email -->
      <div class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Cargo</small>
          <strong>{{ positionName }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Jefe directo</small>
          <strong>{{ supervisorName }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Email corporativo</small>
          <strong>{{ currentContract.corporateEmail || '---' }}</strong>
        </div>
      </div>

      <div class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Canal</small>
          <strong>{{ currentContract.channelName || '---' }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Línea de gasto</small>
          <strong>{{ currentContract.expenseLineName || '---' }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Tipos de planilla</small>
          <template v-if="currentContract.payrollTypeNames && currentContract.payrollTypeNames.length">
            <wz-badge v-for="(name, idx) in currentContract.payrollTypeNames" :key="idx"
              variant="info-solid" class="mr-1">{{ name }}</wz-badge>
          </template>
          <strong v-else>---</strong>
        </div>
      </div>

      <!-- Fechas / Tipo contrato -->
      <div class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Fecha inicio</small>
          <strong v-local-date="currentContract.startDate"></strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Fecha fin</small>
          <strong v-if="currentContract.finishDate" v-local-date="currentContract.finishDate"></strong>
          <strong v-else>Indeterminado</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Tipo de contrato</small>
          <strong>{{ contractTypeLabel }}</strong>
        </div>
      </div>

      <!-- Sueldo / Régimen / Jornada -->
      <div class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Sueldo base</small>
          <strong>{{ formatSalary }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Régimen laboral</small>
          <strong>{{ currentContract.laborRegime || '---' }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Jornada</small>
          <strong>
            {{ currentContract.workSchedule || '---' }}
            <span v-if="currentContract.weeklyHours"> ({{ currentContract.weeklyHours }}h/sem)</span>
          </strong>
        </div>
      </div>

      <!-- Beneficios -->
      <div class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Asignación familiar</small>
          <strong>{{ boolLabel(currentContract.hasFamilyAllowance) }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">EPS</small>
          <strong>{{ boolLabel(currentContract.hasEps) }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">SCTR (Pensión / Salud)</small>
          <strong>{{ boolLabel(currentContract.hasSctrPension) }} / {{ boolLabel(currentContract.hasSctrHealth) }}</strong>
        </div>
      </div>

      <div class="flex flex-wrap mb-3 -mx-2">
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Seguro de vida ley</small>
          <strong>{{ boolLabel(currentContract.hasLifeInsurance) }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2">
          <small class="text-muted block">Código EsSalud</small>
          <strong>{{ currentContract.essaludCode || '---' }}</strong>
        </div>
        <div class="w-full md:w-1/3 px-2" v-if="currentContract.schedule">
          <small class="text-muted block">Horario</small>
          <strong>{{ currentContract.schedule.name || '---' }}</strong>
        </div>
      </div>

      <!-- Documento firmado -->
      <hr class="my-4" />
      <h6 class="text-muted mb-3">Documento firmado</h6>

      <template v-if="currentContract.hasSignedDocument">
        <div class="flex items-center">
          <i :class="signedDocIcon" class="mr-2 text-primary" style="font-size: 1.5rem;"></i>
          <div class="flex-grow">
            <strong>{{ currentContract.signedDocumentName }}</strong>
            <div class="text-muted text-sm" v-if="currentContract.signedDocumentDate">
              Subido el <span v-local-date="currentContract.signedDocumentDate"></span>
            </div>
          </div>
          <wz-button variant="light" size="sm" class="mr-2" @click="downloadSignedDoc" :disabled="downloadingSigned">
            <i class="ri-download-line mr-1"></i>{{ downloadingSigned ? 'Descargando...' : 'Descargar' }}
          </wz-button>
          <wz-button variant="light" size="sm" @click="confirmReplace">
            <i class="ri-refresh-line mr-1"></i>Reemplazar
          </wz-button>
        </div>
      </template>

      <template v-else>
        <div class="flex items-center">
          <span class="text-muted mr-3">No se ha adjuntado el contrato firmado.</span>
          <wz-button variant="primary" size="sm" @click="$refs.signedFileInput.click()">
            <i class="ri-upload-2-line mr-1"></i>Adjuntar contrato firmado
          </wz-button>
        </div>
      </template>

      <input
        type="file"
        ref="signedFileInput"
        accept=".pdf,.jpg,.jpeg,.png"
        style="display: none;"
        @change="onSignedFileSelected"
      />
    </template>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { ContractsService } from '@/core/services'
import { Constants } from '@/core/utils'
import fileDownload from 'js-file-download'

export default {
  name: 'ContractSummaryTab',
  props: {
    employeeId:       { type: Number, default: null },
    currentContracts: { type: Array,  default: () => [] },
  },
  data() {
    return {
      downloadingDoc: false,
      downloadingSigned: false,
      activeContractIndex: 0,
    }
  },
  watch: {
    currentContracts() {
      // Si la lista cambió de tamaño, resetear al primero
      if (this.activeContractIndex >= this.currentContracts.length) {
        this.activeContractIndex = 0
      }
    },
  },
  computed: {
    contracts() {
      return Array.isArray(this.currentContracts) ? this.currentContracts : []
    },
    currentContract() {
      return this.contracts[this.activeContractIndex] || null
    },
    contractStatusBadge() {
      const c = this.currentContract
      if (!c) return { label: 'VIGENTE', color: 'success-solid' }
      switch (c.displayStatus) {
        case 'POR_INICIAR': return { label: 'POR INICIAR', color: 'info-solid' }
        case 'POR_VENCER':  return { label: 'POR VENCER',  color: 'warning-solid' }
        case 'VENCIDO':     return { label: 'VENCIDO',     color: 'danger-solid' }
        case 'CESADO':      return { label: 'CESADO',      color: 'secondary-solid' }
        default:            return { label: 'VIGENTE',     color: 'success-solid' }
      }
    },
    companyName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.company && c.company.name) || c.companyName || '---'
    },
    workSiteName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.workSite && (c.workSite.name || c.workSite.description)) || c.workSiteName || '---'
    },
    areaName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.area && c.area.description) || c.areaName || '---'
    },
    positionName() {
      const c = this.currentContract
      if (!c) return '---'
      return (c.position && c.position.description) || c.positionName || '---'
    },
    supervisorName() {
      const c = this.currentContract
      if (!c) return '---'
      // El DTO expone el jefe directo como directManager { id, description }.
      return (c.directManager && c.directManager.description) || '---'
    },
    contractTypeLabel() {
      const key = this.currentContract ? this.currentContract.contractType : null
      if (!key) return '---'
      const item = Object.values(Constants.ContractType).find(t => t.key === key)
      return item ? item.label : key
    },
    signedDocIcon() {
      const name = (this.currentContract.signedDocumentName || '').toLowerCase()
      if (name.endsWith('.pdf')) return 'ri-file-pdf-line'
      if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')) return 'ri-image-line'
      return 'ri-file-line'
    },
    formatSalary() {
      const c = this.currentContract
      if (!c || c.baseSalary == null) return '---'
      const currency = c.salaryCurrency || 'PEN'
      return `${currency} ${Number(c.baseSalary).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`
    },
  },
  methods: {
    companyOf(c) {
      if (!c) return '---'
      return (c.company && c.company.name) || c.companyName || '---'
    },
    async downloadDocument() {
      try {
        this.downloadingDoc = true
        const response = await ContractsService.downloadDocument(this.currentContract.id)
        fileDownload(response.data, Util.getFileName(response))
      } catch (error) {
        console.error(error)
        Alert.error('Error al generar el documento del contrato')
      } finally {
        this.downloadingDoc = false
      }
    },
    boolLabel(val) {
      if (val === true  || val === 1) return 'Sí'
      if (val === false || val === 0) return 'No'
      return '---'
    },
    async downloadSignedDoc() {
      try {
        this.downloadingSigned = true
        const response = await ContractsService.downloadSignedDocument(this.currentContract.id)
        const name = this.currentContract.signedDocumentName || 'contrato-firmado'
        fileDownload(response.data || response, name)
      } catch (error) {
        console.error(error)
        Alert.error('Error al descargar el documento firmado')
      } finally {
        this.downloadingSigned = false
      }
    },
    async confirmReplace() {
      const ok = confirm('¿Desea reemplazar el documento firmado actual?')
      if (ok) this.$refs.signedFileInput.click()
    },
    async onSignedFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      if (file.size > 100 * 1024 * 1024) {
        Alert.error('El archivo no debe superar 100MB')
        this.$refs.signedFileInput.value = ''
        return
      }
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await ContractsService.uploadSignedDocument(this.currentContract.id, file)
        const updated = resp.payload
        this.currentContract.signedDocumentPath = updated.signedDocumentPath
        this.currentContract.signedDocumentName = updated.signedDocumentName
        this.currentContract.signedDocumentDate = updated.signedDocumentDate
        this.currentContract.hasSignedDocument = updated.hasSignedDocument
        Alert.success('Documento firmado adjuntado correctamente')
      } catch (error) {
        console.error(error)
        Alert.error('Error al subir el documento firmado')
      } finally {
        this.$store.dispatch('app/loading', false)
        this.$refs.signedFileInput.value = ''
      }
    },
  },
}
</script>

<style lang="stylus" scoped></style>
